import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import TournamentForm from '@/modules/tournament/components/TournamentForm'
import TournamentPresetSelector from '@/modules/tournament/components/TournamentPresetSelector'
import TournamentPresetConfigForm from '@/modules/tournament/components/TournamentPresetConfigForm'
import useQueryGetTournamentPresets from '@/modules/tournament/api/hooks/useQueryGetTournamentPresets'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { Info, ArrowLeft } from 'lucide-react'
import { Button } from '@/common/ui/button'

function CreateTournamentPage() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const presetParam = searchParams.get('preset')
	const [selectedPreset, setSelectedPreset] = useState<string | null>(
		presetParam,
	)

	const { data: presetsData, isLoading } = useQueryGetTournamentPresets()

	useEffect(() => {
		if (presetParam) {
			setSelectedPreset(presetParam)
		}
	}, [presetParam])

	const handleSuccess = () => {
		navigate('/operator/tournaments')
	}

	const selectedPresetData = presetsData?.presets?.find(
		(p: { preset: string; name: string; description: string }) =>
			p.preset === selectedPreset,
	)

	const showCustomForm = selectedPreset === 'CUSTOM'

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-start gap-4">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => navigate('/operator/tournaments')}
					>
						<ArrowLeft className="h-4 w-4" />
					</Button>
					<div className="flex-1">
						<h1 className="text-3xl font-bold">สร้างทัวร์นาเมนต์ใหม่</h1>
						<p className="mt-2 text-muted-foreground">
							สร้างทัวร์นาเมนต์เพื่อให้ผู้เล่นแข่งขันและลุ้นรางวัล
						</p>
					</div>
				</div>

				<Alert>
					<Info className="h-4 w-4" />
					<AlertDescription className="text-base">
						เริ่มต้นด้วยการเลือกรูปแบบทัวร์นาเมนต์ที่เหมาะกับคุณ
						หรือสร้างแบบกำหนดเองเพื่อควบคุมค่าทั้งหมดด้วยตัวเอง
					</AlertDescription>
				</Alert>

				{!selectedPresetData && !showCustomForm ? (
					<TournamentPresetSelector
						presets={presetsData?.presets || []}
						selectedPreset={selectedPreset}
						onSelectPreset={setSelectedPreset}
						isLoading={isLoading}
					/>
				) : showCustomForm ? (
					<TournamentForm
						mode="create"
						onSuccess={handleSuccess}
						onCancel={() => setSelectedPreset(null)}
					/>
				) : (
					<TournamentPresetConfigForm
						preset={selectedPresetData!}
						onSuccess={handleSuccess}
						onCancel={() => setSelectedPreset(null)}
					/>
				)}
			</div>
		</PageContainer>
	)
}

export default withUserPermission(CreateTournamentPage, {
	permissionType: PermissionType.Write,
	permissionKey: [PermissionKeys.MANAGE_GAME],
})
